import React from "react";
import { Link } from "react-router";
import { useParams } from "react-router";
import { useNavigate } from "react-router";
import { useLocation } from "react-router";
import { useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { Clock3 } from "lucide-react";
import Button from '../../../shared/ui/Button';
import Modal from '../../../shared/ui/Modal';
import Tabs, { Tab } from '../../../shared/ui/Tabs';

import { useAuth } from "../../auth/AuthContext";
import ItemBasicFields from "../components/ItemBasicFields";
import ItemScheduleForm from "../components/ItemScheduleForm";
import ProcessRecordListPage from "../../process-records/pages/ProcessRecordListPage";

import { ItemInputs, ItemInputsDefault } from "../model/item.types";
import { ItemFormContext } from "../model/ItemFormContext";

import { fetchShopList } from "../../shops/shops.api";
import { fetchItemGroupList } from "../../item-groups/item-groups.api";
import type { ItemGroupSummary } from "../../item-groups/item-groups.api";
import { retrieveUserConfig } from '../../../api/account';
import { resolveSchedule } from "../../../api/schedule";

import { retrieveItem } from "../items.api";
import { createItem } from "../items.api";
import { updateItem } from "../items.api";
import { deleteItem } from "../items.api";
import { duplicateItem } from "../items.api";
import { retrieveItemDuplicates } from "../items.api";



interface ItemEditorProps {
    itemId?: string;
    duplicated?: boolean;
    onClose?: () => void;
    onChanged?: () => void;
    onDeleted?: () => void;
    onDuplicated?: (itemId: string) => void;
    onOpenItem?: (itemId: string) => void;
}

const ItemEditor: React.FC<ItemEditorProps> = ({ itemId, duplicated, onClose, onChanged, onDeleted, onDuplicated, onOpenItem }) => {
    // useAuth
    const auth = useAuth();
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const compareSiteTab = searchParams.get('compare_site') || '';
    const listPath = location.search ? `/items/${location.search}` : '/items/';
    const currentItemId = itemId ?? params.id;
    const isModal = Boolean(onClose);
    const close = () => onClose ? onClose() : navigate(listPath);
    const completeChange = () => {
        if (onChanged) onChanged();
        else navigate(listPath);
    };

    // useState
    const [item, setItem] = React.useState<any>(null);
    const [shop, setShop] = React.useState<any>(null);
    const [shops, setShops] = React.useState<any>(null);
    const [userAdjustConfig, setUserAdjustConfig] = React.useState<any>(null);
    const [itemGroups, setItemGroups] = React.useState<any[]>([]);
    const [prevShopId, setPrevShopId] = React.useState<string>("");
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [processMessage, setProcessMessage] = React.useState<string | null>(null);
    const [showDeleteModal, setshowDeleteModal] = React.useState<boolean>(false);
    const [showDuplicateModal, setShowDuplicateModal] = React.useState<boolean>(false);
    const [duplicateItems, setDuplicateItems] = React.useState<Array<{ id: string, name: string, is_active: boolean }>>([]);
    const [activeTab, setActiveTab] = React.useState<'basic' | 'additional' | 'schedule' | 'history'>('basic')

    const handleItemGroupCreated = React.useCallback((created: ItemGroupSummary) => {
        setItemGroups((current) => [created, ...current.filter((group) => group.id !== created.id)]
            .sort((left, right) => right.sort_order - left.sort_order));
    }, []);
    const [scheduleResolved, setScheduleResolved] = React.useState<any>(false);

    // reactHookForm
    const {
        register,
        handleSubmit,
        watch,
        trigger,
        clearErrors,
        setValue,
        getValues,
        setError, formState: { errors }
    } = useForm<ItemInputs>({
        defaultValues: ItemInputsDefault,
    })

    // useEffect: 데이터 로드: shops, itemGroups
    React.useEffect(() => {
        fetchShopList({
            auth: auth,
            tested: true,
            setErrorMessage,
            setResult: setShops
        })
        fetchItemGroupList({ auth, setErrorMessage, setProcessMessage, setResult: setItemGroups })
    }, [])

    // useEffect: 데이터 로드: adjust configs (user, item defaults)
    React.useEffect(() => {
        retrieveUserConfig({
            auth,
            setErrorMessage,
            setResult: (data: any) => {
                setUserAdjustConfig({
                    adjust_no_exposed: data.adjust_no_exposed,
                    skip_low_coupon: data.skip_low_coupon,
                    adjust_max_step: data.adjust_max_step,
                    reset_max_step: data.reset_max_step,
                    reset_on_ll: data.reset_on_ll,
                    reset_on_ll_countime: data.reset_on_ll_countime,
                });
                Object.entries(data._item_adjust_config_default).forEach(([key, value]) => {
                    setValue(key as keyof ItemInputs, value);
                });
            }
        });
    }, []);

    // useEffect: 새 상품 등록 시 compareSiteTab에서 compare_site 초기화
    React.useEffect(() => {
        if (compareSiteTab && !item) {
            setValue('compare_site', compareSiteTab);
        }
    }, [compareSiteTab, item, setValue]);

    // useEffect: 데이터 로드: item, scheduleResolved
    React.useEffect(() => {
        if (!shops || !currentItemId) return;
        setDuplicateItems([]);
        const setResult = async (data: any) => {
            const formData = {
                ...data,
                extra_config: { ...ItemInputsDefault.extra_config, ...(data.extra_config || {}) },
                compare_pages: data.compare_pages || 1,
                shop_id: data.shop ? data.shop.id : "",
                itemgroup_id: data.itemgroup ? data.itemgroup.id : null,
                my_compare_item_id: data.my_compare_item_id || "",
                my_compare_item: data.my_compare_item || {},
                my_direct_compare_item_id: data.my_direct_compare_item_id || "",
                my_direct_compare_item: data.my_direct_compare_item || {},
                rank_price_basis: data.rank_price_basis || "price_exposed",
                gap_price_basis: data.gap_price_basis || "price_exposed",
                compare_items_specified: data.compare_items_specified || [],
                compare_items_direct_specified: data.compare_items_direct_specified || [],
                // adjust configs: item
                adjust_price_range: data.adjust_price_range,
                adjust_discount_range: data.adjust_discount_range,
                adjust_price_exposed_range: data.adjust_price_exposed_range,
                competition_gap_limit: data.competition_gap_limit,
                // adjust configs: item & users
                adjust_no_exposed: data.adjust_no_exposed,
                skip_low_coupon: data.skip_low_coupon,
                adjust_max_step: data.adjust_max_step,
                reset_max_step: data.reset_max_step,
                reset_on_ll: data.reset_on_ll,
                reset_on_ll_countime: data.reset_on_ll_countime,
            }
            // shop_id 에러 설정 (shop이 없는 경우)
            if (!data.shop) {
                setError('shop_id', { type: 'custom', message: '상점 정보를 찾을 수 없습니다.' });
            }

            setItem(data)

            // setValue로 폼 초기화
            Object.entries(formData).forEach(([key, value]) => {
                if (key !== 'my_compare_item_id' && key !== 'my_direct_compare_item_id') {
                    setValue(key as keyof ItemInputs, value)
                }
            })

            // my_compare_item_id 마지막에 설정하고 검증
            setValue('my_compare_item_id', formData.my_compare_item_id, { shouldValidate: true })
            setValue('my_direct_compare_item_id', formData.my_direct_compare_item_id, { shouldValidate: true })
            await trigger()
        }
        (async () => {
            retrieveItem({
                auth,
                id: currentItemId,
                setErrorMessage,
                setProcessMessage,
                setResult,
            });
            resolveSchedule({
                auth,
                itemId: currentItemId,
                setErrorMessage,
                setProcessMessage,
                setResult: (data: any) => {
                    if (data?.schedule_type) {
                        setScheduleResolved(data);
                    }
                },
            });
            retrieveItemDuplicates({
                auth,
                id: currentItemId,
                setErrorMessage,
                setResult: setDuplicateItems,
            });
        })();
    }, [currentItemId, shops]) // shops가 로드된 후에 실행되도록 의존성 추가


    // userEffect: shop_id 변경 감지 및 shop 객체 설정
    React.useEffect(() => {
        const currentShopId = watch('shop_id');

        if (currentShopId && shops) {
            const foundShop = shops.find((_shop: any) => _shop.id == currentShopId);
            if (foundShop) {
                setShop(foundShop);
            }

            // shop_id가 실제로 변경된 경우에만 관련 필드들 초기화
            if (prevShopId && prevShopId !== currentShopId) {
                setValue("shop_item_id", "");
                setValue("shop_item_gid", "");
                setValue("name", "");
                setValue("price", 0);
                setValue("discount", 0);
                setValue("priced_at", "")
                setValue("shipping_fee", 0);
                setValue("price_exposed", 0);
            }

            // 현재 shop_id를 이전 값으로 저장
            setPrevShopId(currentShopId);
        } else if (!currentShopId) {
            // shop_id가 비어있으면 shop도 초기화
            setShop(null);
        }
    }, [watch('shop_id'), prevShopId, shops]) // shops가 의존성에 포함되어 shops 로드 후에도 실행됨

    // userEffect: premium 권한이 없는 경우 직접지정 범위를 전체상품으로 변경
    React.useEffect(() => {
        if (auth.subscriptionStatus && !auth.subscriptionStatus.item_is_premium && watch('compare_range') === 'items_specified' && watch('compare_site') !== 'direct') {
            setValue('compare_range', 'items_all');
        }
    }, [auth.subscriptionStatus, watch('compare_range')]);

    // handlers
    const onSubmit: SubmitHandler<ItemInputs> = async (data) => {

        const payload = { ...data }
        if (payload.compare_site === 'direct') {
            payload.compare_range = 'items_specified';
        }
        if (item) {
            delete (payload as Partial<ItemInputs>).price;
            delete (payload as Partial<ItemInputs>).discount;
            //console.debug('onSubmit: updateItem()')
            updateItem({
                auth,
                id: item.id,
                payload,
                setProcessMessage,
                setErrorMessage,
                setResult: completeChange,
                setError
            })
        } else {
            //console.debug('onSubmit: createItem()')
            createItem({
                auth,
                payload,
                setProcessMessage,
                setErrorMessage,
                setResult: completeChange,
                setError
            });
        }
    }

    const handleModalDeleteOpen = () => setshowDeleteModal(true)
    const handleModalDeleteClose = () => setshowDeleteModal(false)
    const handleModalDuplicateOpen = () => setShowDuplicateModal(true)
    const handleModalDuplicateClose = () => setShowDuplicateModal(false)
    const handleDuplicate = async () => {
        if (!item?.id) {
            return;
        }
        await duplicateItem({
            auth,
            id: item.id,
            setErrorMessage,
            setProcessMessage,
            setResult: (duplicatedId) => {
                if (onDuplicated) onDuplicated(String(duplicatedId));
                else navigate(`/items/${duplicatedId}/`, { state: { duplicated: true } });
            },
        });
        setShowDuplicateModal(false);
    }
    const handleDelete = async () => {
        if (!item?.id) {
            return;
        }
        await deleteItem({
            auth,
            id: item.id,
            setErrorMessage,
            setProcessMessage,
            setResult: onDeleted ?? completeChange,
        });
        setshowDeleteModal(false);
    }

    const itemActions = item && (<>
        <button type="button" className="btn btn-outline-primary" onClick={handleModalDuplicateOpen}>
            복제
        </button>
        <button type="button" className="btn btn-danger" onClick={handleModalDeleteOpen} disabled={item.is_processing} title={item.is_processing ? '처리 중인 상품은 삭제할 수 없습니다.' : undefined}>
            삭제
        </button>
    </>);

    const content = (
        <ItemFormContext.Provider value={{ register, watch, trigger, clearErrors, setValue, getValues, errors, shop }}>
            <>
                {errorMessage && (
                    <div className="alert alert-danger" role="alert">
                        {errorMessage}
                    </div>
                )}

                {processMessage && (
                    <div className="alert" role="alert">
                        {processMessage}
                    </div>
                )}

                {(duplicated || location.state?.duplicated) && (
                    <div className="alert alert-success" role="alert">
                        복제가 완료되었습니다. 현재 상품은 복제된 상품입니다
                    </div>
                )}

                {!isModal && <div className="page-head page-head-standard d-flex justify-content-between align-items-center mb-0">
                    <div>
                        <p className="fs-5">
                            {item
                                ? `상품변경`
                                : `상품등록`
                            }
                        </p>
                    </div>
                    <div className="d-flex gap-2">
                        <Link to={listPath} className="btn btn-outline-secondary">뒤로가기</Link>
                    </div>
                </div>}

                {scheduleResolved && (
                    <div className="alert alert-light mb-3" role="alert">
                        <Clock3 className="status-icon me-2" aria-hidden="true" />
                        설정된
                        {scheduleResolved.schedule_type === 'user'
                            ? ' 전역 ' :
                            scheduleResolved.schedule_type == 'itemgroup' ? ' 상품그룹 ' :
                                scheduleResolved.schedule_type == 'item' ? ' 상품 ' : null
                        }
                        스케줄이 적용 중인 상품입니다.
                    </div>
                )}

                {duplicateItems.length > 0 && (
                    <div className="alert alert-warning mb-3" role="alert">
                        <div>현재 상품과 중복된 상품이 있습니다.</div>
                        <ul className="mb-0 mt-1">
                            {duplicateItems.map((duplicate) => (
                                <li key={duplicate.id}>
                                    <Link to={`/items/${duplicate.id}/`} onClick={onOpenItem ? (event) => {
                                        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                                        event.preventDefault();
                                        onOpenItem(String(duplicate.id));
                                    } : undefined}>{duplicate.name}</Link>
                                    {` (ID: ${duplicate.id})`}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <form className="form-card" onSubmit={handleSubmit(onSubmit)}>

                    <Tabs
                        activeKey={activeTab}
                        onSelect={(key) => key && setActiveTab(key as typeof activeTab)}
                        ariaLabel="상품 설정"
                    >
                        <Tab eventKey="basic" title="기본 설정">
                            <ItemBasicFields
                                auth={auth}
                                shops={shops}
                                shop={shop}
                                setErrorMessage={setErrorMessage}
                                item={item}
                                itemGroups={itemGroups}
                                onItemGroupCreated={handleItemGroupCreated}
                                userAdjustConfig={userAdjustConfig}
                                scheduleResolved={scheduleResolved}
                            />
                        </Tab>
                        <Tab eventKey="schedule" title="스케줄 설정" disabled={!item}>
                            {item && (
                                <ItemScheduleForm
                                    auth={auth}
                                    itemId={currentItemId || ''}
                                />
                            )}
                        </Tab>
                        {isModal && (
                            <Tab eventKey="history" title="작업이력" disabled={!item}>
                                {item && <ProcessRecordListPage itemId={currentItemId} />}
                            </Tab>
                        )}
                    </Tabs>

                    {(activeTab !== 'history' || item) && <div className="my-2 d-flex justify-content-between gap-2">
                        {activeTab !== 'history' && <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-primary">
                                {item ? '변경' : '등록'}
                            </button>
                            {isModal
                                ? <button type="button" className="btn btn-outline-secondary" onClick={close}>취소</button>
                                : <Link to={listPath} className="btn btn-outline-secondary">취소</Link>}
                        </div>}
                        {item && <div className="d-flex gap-2" style={{ marginLeft: "auto" }}>{itemActions}</div>}
                    </div>}
                </form>

                <Modal show={showDeleteModal} onHide={handleModalDeleteClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>확인</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>삭제하시겠습니까?</Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleModalDeleteClose}>취소</Button>
                        <Button variant="danger" onClick={handleDelete} disabled={item?.is_processing}>삭제</Button>
                    </Modal.Footer>
                </Modal>

                <Modal show={showDuplicateModal} onHide={handleModalDuplicateClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>확인</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>복제하시겠습니까?</Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleModalDuplicateClose}>취소</Button>
                        <Button variant="primary" onClick={handleDuplicate} disabled={processMessage === '복제 중입니다...'}>복제</Button>
                    </Modal.Footer>
                </Modal>
            </>
        </ItemFormContext.Provider>
    )

    if (!isModal) return content;

    return (
        <Modal show onHide={close} size="full">
            <Modal.Header>
                <Modal.Title>상품변경</Modal.Title>
                <div className="d-flex align-items-center gap-2" style={{ marginLeft: "auto" }}>
                    <button type="button" className="btn btn-outline-secondary item-editor-modal-close" onClick={close}>닫기</button>
                </div>
            </Modal.Header>
            <Modal.Body className="item-editor-modal-body">{content}</Modal.Body>
        </Modal>
    );
}

export default ItemEditor;
